import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDate, formatCurrency, getInitials, businessLineColors } from '../lib/utils';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  Phone,
  Mail,
  MapPin,
  Upload,
  Download,
  Cake,
  CalendarDays,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ImportExportManager } from '../components/ImportExportManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Definición de columnas para personalización
const defaultColumns = [
  { id: 'name', label: 'Cliente', visible: true, order: 0 },
  { id: 'contact', label: 'Contacto', visible: true, order: 1 },
  { id: 'contact_date', label: 'Fecha de Contacto', visible: false, order: 2 },
  { id: 'birth_date', label: 'Fecha de Nacimiento', visible: true, order: 3 },
  { id: 'source', label: 'Fuente', visible: false, order: 4 },
  { id: 'business_lines', label: 'Aseguradora', visible: true, order: 5 },
  { id: 'business_types', label: 'Negocio/Plan', visible: true, order: 6 },
  { id: 'aportaciones', label: 'Aportaciones', visible: false, order: 7 },
  { id: 'periodicidad', label: 'Periodicidad', visible: false, order: 8 },
  { id: 'policies_count', label: 'Pólizas', visible: true, order: 9 },
  { id: 'status', label: 'Estado', visible: true, order: 10 },
  { id: 'assigned_agent', label: 'Agente', visible: true, order: 11 },
  { id: 'actions', label: 'Acciones', visible: true, order: 12 }
];

const statusColors = {
  activo: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  inactivo: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
};

export const ClientsPage = () => {
  const { user, canCreate, canEdit, canDelete, isAdmin, isAsistente, isDeveloper } = useAuth();
  const isMaster = user?.role === 'master';
  const { selectedLine, isAllLines, filterByBusinessLine, availableLines } = useBusinessLine();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('contact_date');
  const [sortDir, setSortDir] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birth_date: '',
    contact_date: new Date().toISOString().split('T')[0],
    source: 'Llamada en frío',
    contributions: '',
    periodicidad: 'mensual',
    notes: '',
    business_lines: [],
    business_types: [],
    assigned_agent_id: user?.id || '',
    status: 'activo',
  });
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [agentFilter, setAgentFilter] = useState(user?.id || '');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchDebounced, setSearchDebounced] = useState('');

  // Estado para columnas personalizables
  const [columns, setColumns] = useState(defaultColumns);

  // Debounce search 400ms
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, currentPage, searchDebounced, agentFilter, selectedLine]);

  useEffect(() => {
    axios.get(`${API}/users`).then(r => {
      setUsers(r.data);
      if (isAsistente) {
        const managed = user?.managed_agents || [];
        if (managed.length > 0) {
          setManagedUsers(r.data.filter(u => managed.includes(u.id)));
        }
      }
    }).catch(() => {});
    if (isAdmin || isDeveloper) {
      axios.get(`${API}/agents`).then(r => setAgents(r.data)).catch(() => {});
    }
  }, []);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams({ page: currentPage, page_size: 20 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchDebounced) params.append('search', searchDebounced);
      if ((isAdmin || isAsistente || isDeveloper) && agentFilter && agentFilter !== 'all') params.append('agent_id', agentFilter);
      if (!isAllLines) params.append('business_line', selectedLine);
      const response = await axios.get(`${API}/clients?${params.toString()}`);
      const data = response.data;
      setClients(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        email: formData.email || null,
        contributions: formData.contributions !== '' ? parseFloat(formData.contributions) : 0.0,
      };
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, payload);
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/clients`, payload);
        toast.success('Cliente creado');
      }
      // Cerrar modal primero
      setDialogOpen(false);
      resetForm();
      // Luego refrescar datos
      await fetchClients();
    } catch (error) {
      console.error('Error al guardar cliente:', error);
      const detail = error.response?.data?.detail;
      const msg = detail
        ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
        : 'Error al guardar cliente';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      birth_date: client.birth_date || '',
      contact_date: client.contact_date || '',
      source: client.source || 'Llamada en frío',
      contributions: (client.contributions != null && client.contributions !== 0) ? client.contributions.toString() : '',
      periodicidad: client.periodicidad || 'mensual',
      notes: client.notes || '',
      business_lines: client.business_lines || [],
      business_types: client.business_types || [],
      assigned_agent_id: client.assigned_agent_id || '',
      status: client.status || 'activo',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      birth_date: '',
      contact_date: new Date().toISOString().split('T')[0],
      source: 'manual',
      contributions: '',
      periodicidad: 'mensual',
      notes: '',
      business_lines: selectedLine !== 'all' ? [selectedLine] : [],
      business_types: [],
      assigned_agent_id: user?.id || '',
      status: 'activo',
    });
  };

  // Toggle business line selection
  const toggleBusinessLine = (lineId) => {
    setFormData(prev => ({
      ...prev,
      business_lines: prev.business_lines.includes(lineId)
        ? prev.business_lines.filter(id => id !== lineId)
        : [...prev.business_lines, lineId]
    }));
  };

  // Manejar tipos de negocio/plan
  const [newBusinessType, setNewBusinessType] = useState('');
  
  const addBusinessType = () => {
    const trimmed = newBusinessType.trim();
    if (!trimmed) return;
    setFormData(prev => {
      const types = prev.business_types || [];
      if (types.includes(trimmed)) return prev;
      return { ...prev, business_types: [...types, trimmed] };
    });
    setNewBusinessType('');
  };

  const removeBusinessType = (type) => {
    setFormData(prev => ({
      ...prev,
      business_types: (prev.business_types || []).filter(t => t !== type)
    }));
  };

  // All filtering is server-side; clients is the current page
  const filteredClients = clients;

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline-block flex-shrink-0" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 inline-block flex-shrink-0" />
      : <ArrowDown className="h-3 w-3 ml-1 inline-block flex-shrink-0" />;
  };

  const sortedClients = [...clients].sort((a, b) => {
    const va = (a[sortField] ?? '').toString();
    const vb = (b[sortField] ?? '').toString();
    return sortDir === 'asc' ? va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) : vb.localeCompare(va, undefined, { numeric: true, sensitivity: 'base' });
  });

  const visibleClientCols = [...columns].sort((a, b) => (a.order || 0) - (b.order || 0)).filter(c => c.visible);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground">Gestiona tu cartera de clientes</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2d8659] hover:bg-[#2d8659]/90" data-testid="new-client-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
                <DialogDescription>
                  {editingClient ? 'Modifica la información del cliente' : 'Registra un nuevo cliente'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">Nombre completo *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nombre completo"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="correo@ejemplo.com"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 55 1234 5678"
                      data-testid="client-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birth_date">Fecha de Nacimiento</Label>
                    <Input
                      id="birth_date"
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                      data-testid="client-birth-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_date">Fecha de Contacto</Label>
                    <Input
                      id="contact_date"
                      type="date"
                      value={formData.contact_date}
                      onChange={(e) => setFormData({ ...formData, contact_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source">Fuente</Label>
                    <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona fuente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Llamada en frío">Llamada en frío</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="website">Sitio Web</SelectItem>
                        <SelectItem value="referido">Referido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contributions">Aportaciones (MXN)</Label>
                    <Input
                      id="contributions"
                      type="number"
                      step="0.01"
                      value={formData.contributions}
                      onChange={(e) => setFormData({ ...formData, contributions: e.target.value })}
                      placeholder="0.00"
                      min="0"
                      data-testid="client-contributions-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="periodicidad">Periodicidad</Label>
                    <Select value={formData.periodicidad} onValueChange={(value) => setFormData({ ...formData, periodicidad: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Periodicidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensual">Mensual</SelectItem>
                        <SelectItem value="trimestral">Trimestral</SelectItem>
                        <SelectItem value="semestral">Semestral</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Negocio/Tipo de Plan</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newBusinessType}
                        onChange={(e) => setNewBusinessType(e.target.value)}
                        placeholder="Ej: OptiMaxx Plus, Vida, Auto..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBusinessType())}
                        data-testid="client-business-type-input"
                      />
                      <Button type="button" variant="outline" onClick={addBusinessType}>
                        Agregar
                      </Button>
                    </div>
                    {formData.business_types.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.business_types.map((type) => (
                          <Badge
                            key={type}
                            variant="secondary"
                            className="cursor-pointer hover:bg-red-100"
                            onClick={() => removeBusinessType(type)}
                          >
                            {type} ×
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="notes">Notas</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notas adicionales..."
                      rows={3}
                      data-testid="client-notes-input"
                    />
                  </div>
                  {(isMaster || isAdmin || isAsistente || isDeveloper) ? (
                    <div className="col-span-2 space-y-2">
                      <Label>Agente</Label>
                      <select
                        value={formData.assigned_agent_id || ''}
                        onChange={(e) => setFormData({ ...formData, assigned_agent_id: e.target.value })}
                        className="w-full border rounded px-3 py-2 text-sm bg-background"
                      >
                        <option value="">— Sin asignar —</option>
                        {[...users]
                          .filter(u => ['admin', 'developer', 'agente', 'asesor', 'aspirante'].includes(u.role))
                          .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es'))
                          .map(u => (
                            <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
                          ))
                        }
                      </select>
                    </div>
                  ) : (
                    <div className="col-span-2 space-y-2">
                      <Label>Agente</Label>
                      <div className="w-full border rounded px-3 py-2 text-sm bg-muted text-muted-foreground">
                        {user?.full_name}
                      </div>
                    </div>
                  )}
                  {editingClient && (
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="client_status">Estado del cliente</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(v) => setFormData({ ...formData, status: v })}
                      >
                        <SelectTrigger id="client_status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="activo">Activo</SelectItem>
                          <SelectItem value="inactivo">Inactivo</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                          <SelectItem value="pendiente_de_pago">Pendiente de Pago</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label>Aseguradora *</Label>
                    <div className="flex flex-wrap gap-3 p-3 border rounded-lg">
                      {availableLines.filter(l => l.id !== 'all').map(line => (
                        <div key={line.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`client-line-${line.id}`}
                            checked={formData.business_lines.includes(line.id)}
                            onCheckedChange={() => toggleBusinessLine(line.id)}
                          />
                          <label
                            htmlFor={`client-line-${line.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                          >
                            <div className={`w-2 h-2 rounded-full ${line.color}`}></div>
                            {line.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#1a365d] hover:bg-[#1a365d]/90" 
                    data-testid="client-submit-btn"
                    disabled={formData.business_lines.length === 0 || saving}
                  >
                    {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                    {editingClient ? 'Actualizar' : 'Crear Cliente'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        <div className="flex gap-2">
          <ImportExportManager
            module="clients"
            apiUrl={API}
            data={clients}
            onSuccess={fetchClients}
          />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, email, teléfono o RFC..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="client-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="client-status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activo">Activo</SelectItem>
                <SelectItem value="inactivo">Inactivo</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
              </SelectContent>
            </Select>
            {(isAdmin || isAsistente || isDeveloper) && (
              <Select value={agentFilter} onValueChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="client-agent-filter">
                  <SelectValue placeholder="Agente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user?.id || ''}>Mis Clientes</SelectItem>
                  {!isAsistente && <SelectItem value="all">Todos</SelectItem>}
                  {[...(isAsistente ? managedUsers : agents)]
                    .filter(a => {
                      const uid = isAsistente ? a.id : a.user_id;
                      return uid !== user?.id;
                    })
                    .sort((a, b) => {
                      const na = isAsistente ? (a.full_name || '') : (a.user?.full_name || '');
                      const nb = isAsistente ? (b.full_name || '') : (b.user?.full_name || '');
                      return na.localeCompare(nb, 'es');
                    })
                    .map(a => {
                      const uid = isAsistente ? a.id : a.user_id;
                      const name = isAsistente ? a.full_name : (a.user?.full_name || a.user_id);
                      return (
                        <SelectItem key={uid} value={uid}>
                          {name}
                        </SelectItem>
                      );
                    })
                  }
                </SelectContent>
              </Select>
            )}
            <ColumnCustomizer
              columns={columns}
              storageKey="clients"
              onColumnsChange={setColumns}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table wrapperClassName="overflow-auto max-h-[calc(100vh-16rem)]">
            <TableHeader className="sticky top-0 z-20 bg-white dark:bg-gray-950 border-b shadow-sm">
              <TableRow>
                {visibleClientCols.map(col => {
                  if (col.id === 'name') return (
                    <TableHead key="name" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">Cliente <SortIcon field="name" /></div>
                    </TableHead>
                  );
                  if (col.id === 'contact') return <TableHead key="contact">Contacto</TableHead>;
                  if (col.id === 'contact_date') return (
                    <TableHead key="contact_date" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('contact_date')}>
                      <div className="flex items-center gap-1">Fecha de Contacto <SortIcon field="contact_date" /></div>
                    </TableHead>
                  );
                  if (col.id === 'birth_date') return (
                    <TableHead key="birth_date" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('birth_date')}>
                      <div className="flex items-center gap-1">Fecha Nacimiento <SortIcon field="birth_date" /></div>
                    </TableHead>
                  );
                  if (col.id === 'source') return (
                    <TableHead key="source" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('source')}>
                      <div className="flex items-center gap-1">Fuente <SortIcon field="source" /></div>
                    </TableHead>
                  );
                  if (col.id === 'business_lines') return <TableHead key="business_lines">Aseguradora</TableHead>;
                  if (col.id === 'business_types') return <TableHead key="business_types">Negocio/Plan</TableHead>;
                  if (col.id === 'aportaciones') return <TableHead key="aportaciones">Aportaciones</TableHead>;
                  if (col.id === 'periodicidad') return <TableHead key="periodicidad">Periodicidad</TableHead>;
                  if (col.id === 'policies_count') return <TableHead key="policies_count">Pólizas</TableHead>;
                  if (col.id === 'status') return (
                    <TableHead key="status" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">Estado <SortIcon field="status" /></div>
                    </TableHead>
                  );
                  if (col.id === 'assigned_agent') return (
                    <TableHead key="assigned_agent" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('assigned_agent_name')}>
                      <div className="flex items-center gap-1">Agente <SortIcon field="assigned_agent_name" /></div>
                    </TableHead>
                  );
                  if (col.id === 'actions') return <TableHead key="actions" className="w-[50px]"></TableHead>;
                  return null;
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d] mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                    No se encontraron clientes
                  </TableCell>
                </TableRow>
              ) : (
                sortedClients.map((client) => (
                  <TableRow key={client.id} className="data-row" data-testid={`client-row-${client.id}`}>
                    {visibleClientCols.map(col => {
                      if (col.id === 'name') return (
                        <TableCell key="name">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                                {getInitials(client.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{client.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {formatDate(client.created_at)}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'contact') return (
                        <TableCell key="contact">
                          <div className="space-y-1">
                            {client.email && (
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="h-3 w-3" />
                                {client.email}
                              </div>
                            )}
                            {client.phone && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {client.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'contact_date') return (
                        <TableCell key="contact_date" className="text-sm text-muted-foreground">
                          {client.contact_date ? (
                            <div className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {formatDate(client.contact_date)}
                            </div>
                          ) : <span className="text-xs">-</span>}
                        </TableCell>
                      );
                      if (col.id === 'birth_date') return (
                        <TableCell key="birth_date">
                          {client.birth_date ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Cake className="h-3 w-3 text-pink-500" />
                              {formatDate(client.birth_date)}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'source') return (
                        <TableCell key="source">
                          <span className="text-sm capitalize">{client.source || '-'}</span>
                          {client.source_campaign && (
                            <div className="text-xs text-muted-foreground">{client.source_campaign}</div>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'business_lines') return (
                        <TableCell key="business_lines">
                          <div className="flex flex-wrap gap-1">
                            {(client.business_lines || []).map(lineId => {
                              const line = BUSINESS_LINES.find(l => l.id === lineId);
                              return line ? (
                                <Badge key={lineId} className={`${businessLineColors[lineId]} text-xs`}>
                                  {line.name}
                                </Badge>
                              ) : null;
                            })}
                            {(!client.business_lines || client.business_lines.length === 0) && (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'business_types') return (
                        <TableCell key="business_types">
                          <div className="flex flex-wrap gap-1">
                            {(client.business_types || []).slice(0, 2).map((type, idx) => {
                              const cnt = (client.policies_by_product || {})[type] || 0;
                              const label = cnt > 1 ? `${type} (${cnt})` : type;
                              return (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {label}
                                </Badge>
                              );
                            })}
                            {(client.business_types || []).length > 2 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Badge variant="secondary" className="text-xs cursor-pointer hover:bg-secondary/80">
                                    +{client.business_types.length - 2}
                                  </Badge>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2" align="start">
                                  <div className="flex flex-col gap-1">
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">Planes ocultos:</p>
                                    {client.business_types.slice(2).map((type, idx) => {
                                      const cnt = (client.policies_by_product || {})[type] || 0;
                                      const label = cnt > 1 ? `${type} (${cnt})` : type;
                                      return (
                                        <Badge key={idx} variant="outline" className="text-xs justify-start">
                                          {label}
                                        </Badge>
                                      );
                                    })}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                            {(!client.business_types || client.business_types.length === 0) && (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'aportaciones') return (
                        <TableCell key="aportaciones">
                          {client.contributions ? (
                            <div className="text-sm">
                              <span className="font-medium">${client.contributions?.toLocaleString()}</span>
                              {client.periodicidad && (
                                <span className="text-xs text-muted-foreground ml-1">/{client.periodicidad.slice(0, 3)}</span>
                              )}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                      );
                      if (col.id === 'periodicidad') return (
                        <TableCell key="periodicidad">
                          <span className="text-sm capitalize">{client.periodicidad || '-'}</span>
                        </TableCell>
                      );
                      if (col.id === 'policies_count') return (
                        <TableCell key="policies_count">
                          <Badge variant="secondary">{client.policies_count || 0}</Badge>
                        </TableCell>
                      );
                      if (col.id === 'status') return (
                        <TableCell key="status">
                          <Badge className={`${statusColors[client.status]} border-0 capitalize`}>
                            {client.status}
                          </Badge>
                        </TableCell>
                      );
                      if (col.id === 'assigned_agent') return (
                        <TableCell key="assigned_agent">
                          <span className="text-sm">{client.assigned_agent_name || '—'}</span>
                        </TableCell>
                      );
                      if (col.id === 'actions') return (
                        <TableCell key="actions">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`client-actions-${client.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEdit && (
                                <DropdownMenuItem onClick={() => handleEdit(client)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem>
                                <FileText className="h-4 w-4 mr-2" />
                                Ver pólizas
                              </DropdownMenuItem>
                              {canDelete && (
                                <DropdownMenuItem
                                  onClick={() => handleDelete(client.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      );
                      return null;
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="gap-1">
            <ChevronLeft className="h-4 w-4" />Anterior
          </Button>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Página {currentPage} de {totalPages} &nbsp;·&nbsp; Total: {totalRecords} clientes
          </span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="gap-1">
            Siguiente<ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
